import { useEffect } from 'react';
import { Pencil, SkipForward, CheckCircle, Trash2 } from 'lucide-react';
import type { WeddingEvent } from '@/lib/types';

interface ContextMenuProps {
  event: WeddingEvent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onSkip: () => void;
  onComplete: () => void;
  onDelete: () => void;
}

export function ContextMenu({
  event,
  open,
  onOpenChange,
  onEdit,
  onSkip,
  onComplete,
  onDelete,
}: ContextMenuProps) {
  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const options = [
    {
      icon: <Pencil size={18} style={{ color: 'var(--color-primary)' }} />,
      label: 'Edit Event',
      color: 'var(--color-text)',
      action: onEdit,
    },
    {
      icon: <SkipForward size={18} style={{ color: 'var(--color-warning)' }} />,
      label: 'Skip Event',
      color: 'var(--color-text)',
      action: onSkip,
    },
    {
      icon: <CheckCircle size={18} style={{ color: 'var(--color-success)' }} />,
      label: 'Mark Complete',
      color: 'var(--color-text)',
      action: onComplete,
    },
    {
      icon: <Trash2 size={18} style={{ color: 'var(--color-error)' }} />,
      label: 'Delete Event',
      color: 'var(--color-error)',
      action: onDelete,
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => onOpenChange(false)}
        aria-label="Close menu"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 800,
        }}
      />

      {/* Bottom sheet panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Actions for ${event.name}`}
        className="animate-slide-up"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--color-surface)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '16px 0',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          zIndex: 801,
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: 40,
            height: 4,
            background: 'rgba(148,163,184,0.3)',
            borderRadius: 2,
            margin: '0 auto 16px',
          }}
        />

        {/* Event name */}
        <div
          style={{
            paddingLeft: 16,
            paddingRight: 16,
            marginBottom: 8,
          }}
        >
          <p
            style={{
              fontSize: 13,
              color: 'var(--color-text-secondary)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.name}
          </p>
        </div>

        {/* Option rows */}
        {options.map((option) => (
          <button
            key={option.label}
            onClick={option.action}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              minHeight: 48,
              padding: '0 16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              color: option.color,
              fontFamily: 'var(--font-body)',
              fontSize: 15,
              transition: 'background 150ms ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            {option.icon}
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </>
  );
}
